import classNames from "classnames";
import {ChangeEvent} from "react";
import {RegisterOptions, useFormContext} from "react-hook-form";

import {Card} from "@/components/card/Card.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Switch} from "@/components/switch/Switch.tsx";
import {SwitchInput} from "@/components/switch/SwitchInput/SwitchInput.tsx";
import {SwitchLabel} from "@/components/switch/SwitchLabel/SwitchLabel.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {getRequiredError} from "@/pages/checks/components/form/constants.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

interface SwitchFieldProps extends FieldProps {
  onChange?: (value: boolean) => void;
  description?: string;
  labelPosition?: "left" | "right";
  hideErrorMessage?: boolean;
  variant?: "default" | "card";
}

export const SwitchField = ({
  name,
  label,
  description,
  labelPosition,
  className,
  tooltip,
  supportLink,
  helpText,
  disabled = false,
  hideErrorMessage = false,
  onChange,
  labelTypography,
  required,
  variant = "default",
}: SwitchFieldProps) => {
  const {
    register,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  const extraOptions: RegisterOptions = {
    ...(onChange && {onChange: ({target}: ChangeEvent<HTMLInputElement>) => onChange(target.checked)}),
    ...(required && {required: getRequiredError(name)}),
  };
  return variant === "card" ? (
    <Card
      variant="cloudStatusv2"
      className={classNames(className, "d-flex justify-content-between align-items-center gap-2")}
    >
      <div>
        {label && (
          <SwitchLabel forId={name} helpText={tooltip} className="mb-0" required={required}>
            {label}
          </SwitchLabel>
        )}
        <FormSubtext
          subText={helpText}
          supportLink={supportLink}
          error={hideErrorMessage ? "" : fieldError?.toString()}
        />
      </div>
      <Switch>
        <SwitchInput id={name} {...register(name, extraOptions)} disabled={disabled} />
      </Switch>
    </Card>
  ) : (
    <>
      <Switch
        id={name}
        className={className}
        label={label}
        description={description}
        helpText={tooltip}
        labelPosition={labelPosition}
        labelTypography={labelTypography}
        disabled={disabled}
        required={required}
        {...register(name, extraOptions)}
      />
      <FormSubtext
        subText={helpText}
        supportLink={supportLink}
        error={hideErrorMessage ? "" : fieldError?.toString()}
      />
    </>
  );
};
